'use client';
import React from 'react';
import { AccessibilityInfo, Platform, Text, View, ViewStyle } from 'react-native';
import Animated, { FadeInDown, FadeInUp, FadeOutDown, FadeOutUp } from 'react-native-reanimated';
import { Overlay } from '@gluestack-ui/core/overlay/creator';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { tva, withStyleContext, useStyleContext } from '@gluestack-ui/utils/nativewind-utils';
import type { VariantProps } from '@gluestack-ui/utils/nativewind-utils';

const SCOPE = 'TOAST';

export type IToastPlacement = 'top' | 'bottom' | 'bottom right';

export interface IToastShowConfig {
  placement?: IToastPlacement;
  /** Milliseconds before auto-dismiss; null = persistent. Defaults to 5000. */
  duration?: number | null;
  render: (props: { id: string }) => React.ReactNode;
}

interface IToastEntry {
  id: string;
  placement: IToastPlacement;
  node: React.ReactNode;
}

/**
 * Module-level toast store. Toast state lives outside React so showing or
 * dismissing a toast re-renders only the single ToastHost — never the
 * components that triggered it. show/close are stable module functions.
 */
let toastEntries: IToastEntry[] = [];
const storeListeners = new Set<() => void>();
const hideTimers = new Map<string, ReturnType<typeof setTimeout>>();
let nextToastId = 1;

const notifyToastListeners = () => {
  storeListeners.forEach((listener) => listener());
};

const subscribeToToasts = (listener: () => void) => {
  storeListeners.add(listener);
  return () => {
    storeListeners.delete(listener);
  };
};

const getToastEntries = () => toastEntries;

/**
 * Shows a toast and returns its id.
 * @param config - Placement, auto-dismiss duration, and render function for the toast content
 */
export function showToast({
  placement = 'bottom',
  duration = 5000,
  render,
}: IToastShowConfig): string {
  const id = String(nextToastId++);
  toastEntries = [...toastEntries, { id, placement, node: render({ id }) }];
  if (duration !== null) {
    hideTimers.set(
      id,
      setTimeout(() => closeToast(id), duration)
    );
  }
  notifyToastListeners();
  return id;
}

/**
 * Dismisses a toast by id. No-ops if the toast is already gone.
 * @param id - The id returned by showToast
 */
export function closeToast(id: string) {
  const timer = hideTimers.get(id);
  if (timer) {
    clearTimeout(timer);
    hideTimers.delete(id);
  }
  if (toastEntries.some((entry) => entry.id === id)) {
    toastEntries = toastEntries.filter((entry) => entry.id !== id);
    notifyToastListeners();
  }
}

const toastApi = { show: showToast, close: closeToast };

/**
 * Returns the stable toast API. Kept for compatibility with the previous
 * gluestack hook; calling it subscribes to nothing, so consumers never
 * re-render on toast activity.
 */
const useToast = () => toastApi;

const TOAST_PLACEMENTS: IToastPlacement[] = ['top', 'bottom', 'bottom right'];

const placementContainerStyle: Record<IToastPlacement, ViewStyle> = {
  top: { top: 0, left: 0, right: 0, alignItems: 'center' },
  bottom: { bottom: 0, left: 0, right: 0, alignItems: 'center' },
  'bottom right': { bottom: 0, right: 0, alignItems: 'flex-end' },
};

/**
 * The single component that renders every toast. Mounted once (ThemeProvider)
 * inside the app's OverlayProvider; while toasts exist it registers a gluestack
 * Overlay, which portals into the OverlayProvider's items and therefore paints
 * above anything registered earlier (modals, action sheets, the image viewer).
 */
function ToastHost() {
  const entries = React.useSyncExternalStore(subscribeToToasts, getToastEntries, getToastEntries);
  const insets = useSafeAreaInsets();

  if (entries.length === 0) {
    return null;
  }

  return (
    <Overlay isOpen isKeyboardDismissable={false}>
      {TOAST_PLACEMENTS.map((placement) => {
        const placementEntries = entries.filter((entry) => entry.placement === placement);
        if (placementEntries.length === 0) {
          return null;
        }
        const isTop = placement === 'top';
        return (
          <View
            key={placement}
            pointerEvents="box-none"
            style={[
              {
                position: (Platform.OS === 'web' ? 'fixed' : 'absolute') as 'absolute',
                paddingTop: isTop ? insets.top : 0,
                paddingBottom: isTop ? 0 : insets.bottom,
              },
              placementContainerStyle[placement],
            ]}>
            {placementEntries.map((entry) => (
              <Animated.View
                key={entry.id}
                entering={(isTop ? FadeInUp : FadeInDown).duration(150)}
                exiting={(isTop ? FadeOutUp : FadeOutDown).duration(150)}>
                {entry.node}
              </Animated.View>
            ))}
          </View>
        );
      })}
    </Overlay>
  );
}

const toastStyle = tva({
  base: 'p-4 m-1 rounded-md gap-1 web:pointer-events-auto shadow-hard-5 border-outline-100',
  variants: {
    action: {
      primary: 'bg-primary-700',
      secondary: 'bg-secondary-700',
      error: 'bg-error-800',
      warning: 'bg-warning-700',
      success: 'bg-success-700',
      info: 'bg-info-700',
      muted: 'bg-background-800',
    },

    variant: {
      solid: '',
      outline: 'border bg-background-0',
    },
  },
});

const toastTitleStyle = tva({
  base: 'text-typography-0 font-medium font-body tracking-md text-left',
  variants: {
    isTruncated: {
      true: '',
    },
    bold: {
      true: 'font-bold',
    },
    underline: {
      true: 'underline',
    },
    strikeThrough: {
      true: 'line-through',
    },
    size: {
      '2xs': 'text-2xs',
      xs: 'text-xs',
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
      '2xl': 'text-2xl',
      '3xl': 'text-3xl',
      '4xl': 'text-4xl',
      '5xl': 'text-5xl',
      '6xl': 'text-6xl',
    },
  },
  parentVariants: {
    variant: {
      solid: '',
      outline: '',
    },
    action: {
      error: '',
      warning: '',
      success: '',
      info: '',
      muted: '',
    },
  },
  parentCompoundVariants: [
    {
      variant: 'outline',
      action: 'error',
      class: 'text-error-800',
    },
    {
      variant: 'outline',
      action: 'warning',
      class: 'text-warning-800',
    },
    {
      variant: 'outline',
      action: 'success',
      class: 'text-success-800',
    },
    {
      variant: 'outline',
      action: 'info',
      class: 'text-info-800',
    },
    {
      variant: 'outline',
      action: 'muted',
      class: 'text-background-800',
    },
  ],
});

const toastDescriptionStyle = tva({
  base: 'font-normal font-body tracking-md text-left',
  variants: {
    isTruncated: {
      true: '',
    },
    bold: {
      true: 'font-bold',
    },
    underline: {
      true: 'underline',
    },
    strikeThrough: {
      true: 'line-through',
    },
    size: {
      '2xs': 'text-2xs',
      xs: 'text-xs',
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
      '2xl': 'text-2xl',
      '3xl': 'text-3xl',
      '4xl': 'text-4xl',
      '5xl': 'text-5xl',
      '6xl': 'text-6xl',
    },
  },
  parentVariants: {
    variant: {
      solid: 'text-typography-50',
      outline: 'text-typography-900',
    },
  },
});

const Root = withStyleContext(View, SCOPE);
type IToastProps = React.ComponentProps<typeof Root> & {
  className?: string;
} & VariantProps<typeof toastStyle>;

const Toast = React.forwardRef<React.ComponentRef<typeof Root>, IToastProps>(function Toast(
  { className, variant = 'solid', action = 'muted', ...props },
  ref
) {
  return (
    <Root
      ref={ref}
      className={toastStyle({ variant, action, class: className })}
      context={{ variant, action }}
      {...props}
    />
  );
});

type IToastTitleProps = React.ComponentProps<typeof Text> & {
  className?: string;
} & VariantProps<typeof toastTitleStyle>;

const ToastTitle = React.forwardRef<React.ComponentRef<typeof Text>, IToastTitleProps>(
  function ToastTitle({ className, size = 'md', children, ...props }, ref) {
    const { variant: parentVariant, action: parentAction } = useStyleContext(SCOPE);
    React.useEffect(() => {
      // Issue from react-native side
      // Hack for now, will fix this later
      AccessibilityInfo.announceForAccessibility(children as string);
    }, [children]);

    return (
      <Text
        {...props}
        ref={ref}
        aria-live="assertive"
        aria-atomic="true"
        role="alert"
        className={toastTitleStyle({
          size,
          class: className,
          parentVariants: {
            variant: parentVariant,
            action: parentAction,
          },
        })}>
        {children}
      </Text>
    );
  }
);

type IToastDescriptionProps = React.ComponentProps<typeof Text> & {
  className?: string;
} & VariantProps<typeof toastDescriptionStyle>;

const ToastDescription = React.forwardRef<React.ComponentRef<typeof Text>, IToastDescriptionProps>(
  function ToastDescription({ className, size = 'md', ...props }, ref) {
    const { variant: parentVariant } = useStyleContext(SCOPE);
    return (
      <Text
        ref={ref}
        {...props}
        className={toastDescriptionStyle({
          size,
          class: className,
          parentVariants: {
            variant: parentVariant,
          },
        })}
      />
    );
  }
);

Toast.displayName = 'Toast';
ToastTitle.displayName = 'ToastTitle';
ToastDescription.displayName = 'ToastDescription';

export { useToast, ToastHost, Toast, ToastTitle, ToastDescription };
